import { io } from "socket.io-client";
import { getTournamentRanking } from "./tournamentService";
export let socket;
let prevCoin;

const tradeTypeEventMap = {
  Spot: "refreshTournamentRankingWithTotalInUSDT",
  Margin: "refreshMarginTournamentRankingWithTotalInUSDT",
};

const tradeTypeRankingUpdatedEventMap = {
  Spot: "rankingUpdated",
  Margin: "rankingUpdated",
};

export const initializeSocket = async (token, tournamentId, onConnect, forceReconnect = false) => {
  return new Promise((resolve, reject) => {
    if (!process.env.REACT_APP_SOCKET_ENDPOINT) {
      reject(new Error('REACT_APP_SOCKET_ENDPOINT is not configured'));
      return;
    }

    // Always disconnect if forcing reconnect
    if (forceReconnect && socket) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Force disconnecting existing socket');
      }
      socket.disconnect();
      socket = null;
    }

    if (!socket?.connected && token) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Creating new socket connection...');
      }

      // TEAM_007: If we have a stale disconnected socket instance, tear it down before creating a new one.
      if (socket && !socket.connected) {
        try {
          socket.disconnect();
        } catch (error) {
          // ignore
        }
        socket = null;
      }

      const tournamentQuery = {};
      if (
        tournamentId !== undefined &&
        tournamentId !== null &&
        tournamentId !== '' &&
        !Number.isNaN(Number(tournamentId))
      ) {
        tournamentQuery.tournamentId = tournamentId;
      }

      const attachCommonListeners = () => {
        socket.on("connect", () => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Socket connected, calling onConnect callback');
          }
          // TEAM_007: Join tournament rooms via explicit event (new flow).
          if (tournamentId !== undefined && tournamentId !== null && tournamentId !== '') {
            const numericTournamentId = Number(tournamentId);
            socket.emit('joinTournament', {
              tournamentId: Number.isNaN(numericTournamentId) ? tournamentId : numericTournamentId,
            });
          }
          if (onConnect) {
            onConnect();
          }
          resolve(socket);
        });

        socket.on("disconnect", (reason) => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Socket disconnected:', reason);
          }
        });

        // SECURITY: previously fell back to `query: { token }` on connect_error.
        // That path leaked the JWT into Heroku router logs, Referer headers and
        // browser history. The backend has handled `auth.token` since Socket.IO 3.x
        // (2020) — there is no live consumer of the legacy query-token flow.
        socket.on("connect_error", (error) => {
          console.error('Socket connection error:', error);
          reject(error);
        });
      };

      // TEAM_007: Prefer handshake.auth.token (no query token by default).
      try {
        socket = io(process.env.REACT_APP_SOCKET_ENDPOINT, {
          withCredentials: true,
          transports: ["websocket"],
          auth: { token },
          query: tournamentQuery,
        });
      } catch (socketInitError) {
        reject(socketInitError);
        return;
      }

      attachCommonListeners();
    } else if (socket?.connected) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Socket already connected, calling onConnect callback');
      }
      // TEAM_007: If we reuse an existing socket across routes, ensure we join the tournament rooms.
      if (tournamentId !== undefined && tournamentId !== null && tournamentId !== '') {
        const numericTournamentId = Number(tournamentId);
        socket.emit('joinTournament', {
          tournamentId: Number.isNaN(numericTournamentId) ? tournamentId : numericTournamentId,
        });
      }
      if (onConnect) {
        onConnect();
      }
      resolve(socket);
    } else {
      reject(new Error('Socket token is required to initialize a new connection'));
    }
  });
};

// TEAM_007: Explicit join flow helper for tournament rooms.
export const joinTournamentRooms = (tournamentId) => {
  if (!socket || tournamentId === undefined || tournamentId === null || tournamentId === '') {
    return;
  }
  const numericTournamentId = Number(tournamentId);
  socket.emit('joinTournament', {
    tournamentId: Number.isNaN(numericTournamentId) ? tournamentId : numericTournamentId,
  });
};

export const setTournamentStatusChangedListener = (callback) => {
  if (socket) {
    socket.on('tournamentStatusChanged', callback);
  }
};

export const removeTournamentStatusChangedListener = () => {
  if (socket) {
    socket.off('tournamentStatusChanged');
  }
};

export const setOpponentJoinedListener = (callback) => {
  if (socket) {
    socket.on('opponentJoined', callback);
  }
};

export const removeOpponentJoinedListener = () => {
  if (socket) {
    socket.off('opponentJoined');
  }
};

export const makeTrade = (tradePayload, callback) => {
  if (socket) {
    socket.emit("makeTrade", tradePayload, callback);
  }
};

export const makeMargin = (tradePayload, callback) => {
  if (socket) {
    socket.emit("makeMargin", tradePayload, callback);
  }
};

export const closeMargin = (tradeId, callback) => {
  if (socket) {
    socket.emit("closeMargin", { id: tradeId }, callback);
  }
};

export const cancelMargin = (tradeId, callback) => {
  if (socket) {
    socket.emit("cancelMargin", { id: tradeId }, callback);
  }
};

export const editMargin = (editPayload, callback) => {
  if (socket) {
    socket.emit("editMargin", editPayload, callback);
  }
};

export const getAllCoinsPrices = (
  coins,
  updateCoinsValue,
) => {
  const dataArray = {}
  if (socket) {
    const updateData = (currency) => (data) => {
      dataArray[currency] = {"price":data?.last, "data":data};
      updateCoinsValue({"key": currency, "data":data})
    };
    coins.forEach(coin => {
      let coinShortName = coin.attributes.name.split('/')[0].toLowerCase();

      socket.on(
        `${coinShortName}PriceUpdate`,
        updateData(coinShortName)
      );
      socket.on(
        `${coinShortName}OHLCV`,
        updateData(`${coinShortName}OHLCV`)
      );
    });
  }
}

export const getPriceBySelectedCoin = (
  selectedCoin,
  updateCoinsValue
) => {
  if (socket) {
    if(prevCoin && prevCoin !== selectedCoin){
      //socket.off(`${prevCoin}PriceUpdate`);
    }
    prevCoin = selectedCoin;
    const createPriceUpdateListener = (currency, updatePrice, save24hData) => (data) => {
      updateCoinsValue({"key":selectedCoin, "data":data});
    };
    socket.on(
      `${selectedCoin}PriceUpdate`,
      createPriceUpdateListener(`${selectedCoin}Test`, updateCoinsValue)
    );
  }
}

export const removePriceUpdateListeners = () => {
  if (socket) {
    socket.off("btcPriceUpdate");
    socket.off("ethPriceUpdate");
    socket.off("avaxPriceUpdate");
    socket.off("adaPriceUpdate");
    socket.off("bnbPriceUpdate");
    socket.off("btcOHLCV");
  }
};

// TEAM_004: Global listener helpers for tournamentFinished broadcast.
// Backend emits tournamentFinished to room "tournamentId" when a tournament ends.
// Frontend should attach a single listener per socket lifecycle and filter by tournamentId.
export const setTournamentFinishedListener = (callback) => {
  if (socket) {
    socket.on("tournamentFinished", callback);
  }
};

export const removeTournamentFinishedListener = () => {
  if (socket) {
    socket.off("tournamentFinished");
  }
};

// Backend rejects user-initiated margin mutations (close/edit/cancel) after the
// tournament leaves the active trading window. Frontend listens for the reject
// event to show a toast and force a context refresh so the UI reflects the
// inactive phase.
export const setMarginActionRejectedListener = (callback) => {
  if (socket) {
    socket.on("marginActionRejected", callback);
  }
};

export const removeMarginActionRejectedListener = () => {
  if (socket) {
    socket.off("marginActionRejected");
  }
};

export const disconnectSocket = (tradeType) => {
  if (socket) {
    removePriceUpdateListeners();
    removeRefreshTournamentRankingListener(tradeType);
    removeBtcOHLCVListener();
    removeTournamentFinishedListener();
    removeTournamentStatusChangedListener();
    removeOpponentJoinedListener();
    removeMarginActionRejectedListener();
    socket.disconnect();
    socket = null;
  }
};

const tradeTypeMap = {
  Spot: "getTrading",
  Margin: "marginHistory"
};

export const setTradingUpdateListener = (tradeType, updateTrading) => {
  if (socket) {
    const eventName = tradeTypeMap[tradeType];

    if (eventName) {
      socket.on(eventName, (data) => {
        updateTrading(data);
      });
    }
  }
};

export const removeTradingUpdateListener = (tradeType) => {
  if (socket) {
    const eventName = tradeTypeMap[tradeType];
    if (eventName) {
      socket.off(eventName);
    }
  }
};

const tradeTypeEmitMap = {
  Spot: "getTrading",
  Margin: "historyMargin"
};

// TEAM_004: Support margin history payload with tradingPair for historyMargin
export const requestTradingData = (tournamentId, tradeType, callback, tradingPair) => {
  if (!socket) return;
  const eventName = tradeTypeEmitMap[tradeType];
  if (!eventName) {
    return;
  }

  let payload;

  if (tradeType === "Margin") {
    // For margin history, backend can work with empty payload {} or with explicit tradingPair
    if (typeof tradingPair === "string" && tradingPair.length > 0) {
      payload = { tradingPair };
    } else {
      payload = {};
    }
  } else {
    if (!tournamentId) return;
    payload = { tournament: tournamentId };
  }

  socket.emit(eventName, payload, callback);
};

export const requestWalletData = (tournamentId, callback) => {
  if (!tournamentId || !socket) return;
  socket.emit("getWallets", { tournament: tournamentId }, callback);
};

export const emitRefreshTournamentRanking = (tradeType, data, callback) => {
  const eventName = tradeTypeEventMap[tradeType];
  if (socket && eventName) {
    socket.emit(eventName, data, callback);
  }
};

export const setRefreshTournamentRankingListener = async (
  tradeType,
  tournamentId,
  callback
) => {
  const eventName = tradeTypeEventMap[tradeType];
  const rankingUpdatedEventName = tradeTypeRankingUpdatedEventMap[tradeType];

  if (!socket) {
    return;
  }

  const handler = async (data) => {
    try {
      if (!data || Object.keys(data).length === 0) {
        return;
      }

      const apiData = await getTournamentRanking(tournamentId);
      if (apiData) {
        callback(apiData);
      }
    } catch (error) {
      console.error("Error fetching tournament ranking:", error);
    }
  };

  if (eventName) {
    socket.on(eventName, handler);
  }

  // TEAM_007: New backend can emit rankingUpdated instead of legacy refresh* events.
  if (rankingUpdatedEventName) {
    socket.on(rankingUpdatedEventName, handler);
  }
};

export const removeRefreshTournamentRankingListener = (tradeType) => {
  const eventName = tradeTypeEventMap[tradeType];
  const rankingUpdatedEventName = tradeTypeRankingUpdatedEventMap[tradeType];
  if (socket && eventName) {
    socket.off(eventName);
  }
  if (socket && rankingUpdatedEventName) {
    socket.off(rankingUpdatedEventName);
  }
};

export const setBtcOHLCVListener = (callback) => {
  if (socket) {
    socket.on("btcOHLCV", (data) => {
      callback(data);
    });
  }
};

export const removeBtcOHLCVListener = () => {
  if (socket) {
    socket.off("btcOHLCV");
  }
};

export const emitChangeOHLCVTimeframe = (payload, callback) => {
  if (socket) {
    socket.emit("changeOHLCVtimeframe", payload, callback);
  }
};
